import { useEffect } from "react";
import { Link } from "react-router-dom";
import { X, ArrowUpRight } from "lucide-react";
import PixelPlaceholder from "./PixelPlaceholder";

/**
 * QuickView — compact floating panel used ONLY for shop items now.
 * Artists / releases / projects click directly through to their dedicated pages;
 * the modal introduced an unnecessary navigation step for them.
 *
 * The panel is deliberately small (max-w-md), scrollable, and sits centered with
 * proper top clearance so it never feels like a full-screen page.
 */
export default function QuickView({ item, onClose }) {
    useEffect(() => {
        if (!item) return undefined;
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [item, onClose]);

    if (!item) return null;
    const {
        kind = "",
        title,
        subtitle,
        description,
        image_url,
        credits = [],
        tags = [],
        price,
        currency = "USD",
        product_url,
        product_url_label,      // e.g. "Shop on Etsy ↗"
        primary_link,           // { href, label } — usually "View Product ↗" to a dedicated page
    } = item;

    return (
        <div
            data-testid="quickview-overlay"
            onClick={onClose}
            className="fixed inset-0 z-[120] bg-black/70 flex items-center justify-center p-4 overflow-y-auto"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="relative bg-white text-[#111] w-full max-w-md max-h-[85vh] overflow-y-auto shadow-2xl"
                data-testid="quickview-panel"
            >
                {/* Sticky close — never lost when scrolling */}
                <button
                    onClick={onClose}
                    aria-label="close"
                    data-testid="quickview-close"
                    className="sticky top-0 float-right z-10 m-3 bg-white hover:bg-[#F0C56C] text-[#111] w-8 h-8 flex items-center justify-center border border-[#111] transition-colors"
                >
                    <X size={14} />
                </button>

                <div className="clear-both">
                    {/* Media */}
                    <div className="w-full bg-[#F5F5F5] aspect-square overflow-hidden">
                        {image_url ? (
                            <img
                                src={image_url}
                                alt={title || ""}
                                className="w-full h-full object-cover"
                                referrerPolicy="no-referrer"
                            />
                        ) : (
                            <PixelPlaceholder seed={title || kind} size={14} />
                        )}
                    </div>

                    {/* Body */}
                    <div className="px-5 py-5">
                        {(kind || tags[0]) && (
                            <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#777]">
                                {[kind, tags[0]].filter(Boolean).join(" · ")}
                            </div>
                        )}
                        {title && (
                            <h2 className="mt-2 font-heading font-black text-xl md:text-2xl uppercase tracking-tight leading-[1.1] text-[#111]">
                                {title}
                            </h2>
                        )}
                        {subtitle && (
                            <div className="mt-1 font-body italic text-sm text-[#666]">
                                {subtitle}
                            </div>
                        )}

                        {typeof price === "number" && !isNaN(price) && (
                            <div className="mt-4 font-heading font-black text-lg">
                                {currency === "EUR" ? "€" : "$"}{Number(price).toFixed(2)}
                            </div>
                        )}

                        {description && (
                            <p className="mt-4 font-body text-sm leading-relaxed text-[#333] whitespace-pre-line max-w-[52ch]">
                                {description}
                            </p>
                        )}

                        {credits.length > 0 && (
                            <div className="mt-4 space-y-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[#555]">
                                {credits.map((c, i) =>
                                    c.href ? (
                                        c.href.startsWith("/") ? (
                                            <div key={i}>
                                                <Link to={c.href} onClick={onClose} className="hover:text-[#5D3FD3] transition-colors inline-flex items-center gap-1">
                                                    {c.label} <ArrowUpRight size={10} />
                                                </Link>
                                            </div>
                                        ) : (
                                            <div key={i}>
                                                <a href={c.href} target="_blank" rel="noreferrer" className="hover:text-[#5D3FD3] transition-colors inline-flex items-center gap-1">
                                                    {c.label} <ArrowUpRight size={10} />
                                                </a>
                                            </div>
                                        )
                                    ) : (
                                        <div key={i}>{c.label}</div>
                                    )
                                )}
                            </div>
                        )}

                        <div className="mt-6 flex flex-col gap-2">
                            {product_url && (
                                <a
                                    href={product_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="quickview-open-shop"
                                    className="inline-flex items-center justify-center gap-2 bg-[#5D3FD3] text-white px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.28em] hover:bg-[#111] transition-colors"
                                >
                                    {product_url_label || "shop on etsy"} <ArrowUpRight size={12} />
                                </a>
                            )}
                            {primary_link && primary_link.href && (
                                primary_link.href.startsWith("/") ? (
                                    <Link
                                        to={primary_link.href}
                                        onClick={onClose}
                                        data-testid="quickview-primary"
                                        className="inline-flex items-center justify-center gap-2 border border-[#111] hover:bg-[#111] hover:text-white transition-colors px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.28em]"
                                    >
                                        {primary_link.label || "view product"} <ArrowUpRight size={12} />
                                    </Link>
                                ) : (
                                    <a
                                        href={primary_link.href}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid="quickview-primary"
                                        className="inline-flex items-center justify-center gap-2 border border-[#111] hover:bg-[#111] hover:text-white transition-colors px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.28em]"
                                    >
                                        {primary_link.label || "open"} <ArrowUpRight size={12} />
                                    </a>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
